export interface Action {
    id: string;
    namespace: string;
    icon?: string;
    tags?: string[];
    name: string;
    description?: string;
    category?: string;
    orgId: string;
    visibility: ActionVisibility;
    status: ActionStatus;
    publishedRevision?: ActionRevision;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
}

export interface ActionRevision {
    id: string;
    version: number;
    url: string;
    inputs?: ActionParameter[];
    outputs?: ActionParameter[];
    status: ActionStatus;
    supportedEvents?: string;
    views?: ActionView[];
    actionId?: string;
}

export interface ActionParameter {
    name: string;
    type: string;
    description?: string;
    required?: boolean;
    default?: any;
}

export interface ActionView {
    id: string;
    type: string;
    config?: Record<string, any>;
}

export enum ActionStatus {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    PREVIOUSLY_PUBLISHED = 'PREVIOUSLY_PUBLISHED',
    ARCHIVED = 'ARCHIVED',
    DELETED = 'DELETED',
}

export enum ActionVisibility {
    PUBLIC = 'PUBLIC',
    ORG = 'ORG',
    ECOSYSTEM = 'ECOSYSTEM',
}

export interface ActionWithRevisions extends Action {
    revisions?: ActionRevision[];
    currentRevision?: ActionRevision;
}

// Query/fetch params
export interface FetchActionsParams {
    orgId?: string;
    namespace?: string;
}

export interface CreateActionParams {
    namespace: string;
    name: string;
    description?: string;
    orgId: string;
    visibility?: ActionVisibility;
    icon?: string;
    tags?: string[];
}
export interface UpdateActionParams {
    id: string;
    namespace?: string;
    name?: string;
    description?: string;
    visibility?: ActionVisibility;
    icon?: string;
    tags?: string[];
}

export interface DeleteActionParams {
    id: string;
}

// Revision params
export interface FetchActionRevisionsParams {
    actionId: string;
}

export interface CreateRevisionParams {
    actionId?: string;
    version: number;
    url: string;
    status?: ActionStatus;
    inputs?: ActionParameter[];
    outputs?: ActionParameter[];
    supportedEvents?: string;
    views?: ActionView[];
}

export interface UpdateRevisionParams {
    id: string;
    version?: number;
    url?: string;
    status?: ActionStatus;
    inputs?: ActionParameter[];
    outputs?: ActionParameter[];
    supportedEvents?: string;
    views?: ActionView[];
}

export interface DeleteRevisionParams {
    id: string;
}

export interface ActionDisplay {
    id: string;
    name: string;
    namespace: string;
    description: string;
    lastModified: string | null;
    status: ActionStatus;
    // Highlighting information for search results
    _highlighted?: boolean;
    _highlightmap?: Record<string, any>;
}

// Execution types
export enum ExecutionStatus {
    COMPLETED = 'COMPLETED',
    STARTED = 'STARTED',
    WAITING = 'WAITING',
    CANCELLED = 'CANCELLED',
    BYPASSED = 'BYPASSED',
    ERROR = 'ERROR',
    SKIPPED = 'SKIPPED',
}

export interface ObjectMapping {
    object: string;
    objectId: string;
}

export interface Execution {
    id: string;
    orgId: string;
    publicName: string;
    description?: string;
    tags?: string[];
    status: ExecutionStatus;
    customStatus?: string;
    startedDate?: string;
    completedDate?: string;
    lastCommunicationDate?: string;
    action?: Action;
    callbackId?: string;
    inputs?: Record<string, unknown>;
    outputs?: Record<string, unknown>;
    state?: Record<string, unknown>;
    log?: Array<Record<string, unknown>>;
    flowControl?: Record<string, string>;
    objectMapping?: ObjectMapping[];
    createdAt?: string;
    updatedAt?: string;
}

// Execution params
export interface FetchExecutionsParams {
    orgId?: string;
    object?: string;
    objectId?: string;
    limit?: number;
}

export interface CreateExecutionParams {
    orgId: string;
    publicName: string;
    description?: string;
    tags?: string[];
    status?: ExecutionStatus;
    actionId?: string;
    action?: Action;
    inputs?: Record<string, unknown>;
    outputs?: Record<string, unknown>;
    flowControl?: Record<string, string>;
    objectMapping?: ObjectMapping[];
}

export interface UpdateExecutionParams {
    id: string;
    actionId?: string;
    status?: ExecutionStatus;
    customStatus?: string;
    state?: Record<string, unknown>;
    outputs?: Record<string, unknown>;
}

export interface CancelExecutionParams {
    executionId: string;
    callbackId: string;
}

/** Callback event types for execution lifecycle management */
export enum CallbackEventType {
    START = 'START',
    HEALTH = 'HEALTH',
    COMPLETE = 'COMPLETE',
    ERROR = 'ERROR',
    ACTION_STATE = 'ACTION_STATE',
    CANCEL = 'CANCEL',
    UPDATE = 'UPDATE',
}

export interface CallbackEventParams {
    type: CallbackEventType;
    status?: string;
    data?: Record<string, unknown>;
}
